/**
 * Figaro Workscopes — dashboard, MPD library, evaluation detail.
 * Routes: #/figaro/workscopes, #/figaro/workscopes/mpds, #/figaro/workscopes/:id
 */
import { guessFleetFromModel, revisionMatchesFleet } from "./workscopes-fleet.mjs";
import {
  extractsFromLeaseConditions,
  renderDocExtractsViewer,
} from "./figaro-doc-extracts-viewer.jsx";

const STYLE_ID = "workscopes-styles";
const questionsAutoTried = new Set();
const clausesAutoTried = new Set();

function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
.ws-status { display:inline-flex; align-items:center; gap:.35rem; padding:.15rem .55rem; border-radius:.4rem; font-size:.72rem; font-weight:600; border:1px solid; }
.ws-status-IN_PROGRESS { background:#fff7ed; color:#c2410c; border-color:#fdba74; }
.ws-status-RETURNED_TO_OPERATOR { background:#eff6ff; color:#1d4ed8; border-color:#93c5fd; }
.ws-status-APPROVED { background:#ecfdf5; color:#047857; border-color:#a7f3d0; }
.ws-status-CANCELLED { background:#f9fafb; color:#6b7280; border-color:#e5e7eb; }
.ws-rev { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-weight:700; color:#b91c1c; background:#fef2f2; border:1px solid #fecaca; border-radius:.35rem; padding:.1rem .4rem; font-size:.78rem; }
.ws-table { width:100%; border-collapse:collapse; font-size:.8125rem; }
.ws-table th { text-align:left; color:#fff; font-weight:600; padding:.5rem .65rem; border-bottom:1px solid rgba(0,0,0,.12); white-space:nowrap; position:sticky; top:0; background:#b91c1c; z-index:1; font-size:.7rem; letter-spacing:.04em; text-transform:uppercase; }
.ws-table td { padding:.45rem .6rem; border-bottom:1px solid #f3f4f6; vertical-align:top; color:#111827; }
.ws-card { background:#fff; border:1px solid #e5e7eb; border-radius:.75rem; box-shadow:0 1px 2px rgba(16,24,40,.04); }
.ws-card.is-dim { opacity:.55; }
.ws-card.is-focus { border-color:#fecaca; box-shadow:0 0 0 2px rgba(185,28,28,.12); }
.ws-callout { border:1px solid #fde68a; background:#fffbeb; border-radius:.75rem; padding:1rem 1.1rem; }
.ws-drop { border:2px dashed #d1d5db; border-radius:.75rem; padding:1.1rem 1rem; text-align:center; color:#6b7280; font-size:.8125rem; cursor:pointer; transition:border-color .15s, background .15s; }
.ws-drop:hover, .ws-drop.is-drag { border-color:#b91c1c; background:rgba(185,28,28,.04); color:#374151; }
.ws-plus { display:inline-flex; align-items:center; justify-content:center; width:1.75rem; height:1.75rem; border-radius:.5rem; border:1px solid #e5e7eb; background:#fff; color:#b91c1c; font-size:1.15rem; line-height:1; font-weight:500; cursor:pointer; }
.ws-plus:hover { background:#fef2f2; border-color:#fecaca; }
.ws-icon-btn { display:inline-flex; align-items:center; justify-content:center; width:1.75rem; height:1.75rem; border-radius:.45rem; border:1px solid #e5e7eb; background:#fff; color:#374151; font-size:.85rem; cursor:pointer; text-decoration:none; }
.ws-icon-btn:hover { background:#f9fafb; border-color:#d1d5db; }
.ws-icon-btn-danger { color:#b91c1c; }
.ws-icon-btn-danger:hover { background:#fef2f2; border-color:#fecaca; }
.ws-stage-rail-wrap { position:sticky; top:0; z-index:30; background:linear-gradient(180deg,#f9fafb 70%,rgba(249,250,251,.92)); padding:.65rem 0 .55rem; margin:0 -0.15rem; border-bottom:1px solid #e5e7eb; }
.ws-stage-rail { display:grid; grid-template-columns:repeat(6,minmax(0,1fr)); gap:.4rem; }
@media (max-width:1100px) { .ws-stage-rail { grid-template-columns:repeat(3,minmax(0,1fr)); } }
@media (max-width:700px) { .ws-stage-rail { grid-template-columns:repeat(2,minmax(0,1fr)); } }
.ws-stage-btn { border:1px solid #e5e7eb; cursor:pointer; text-align:left; background:#fff; border-radius:.65rem; padding:.55rem .6rem; transition:border-color .15s, background .15s, box-shadow .15s; }
.ws-stage-btn:hover { border-color:#d1d5db; background:#f9fafb; }
.ws-stage-btn.is-on { border-color:#b91c1c; background:#fef2f2; box-shadow:0 0 0 1px #b91c1c; }
.ws-stage-btn.is-done { border-color:#a7f3d0; background:#ecfdf5; }
.ws-stage-letter { font-size:.65rem; font-weight:700; letter-spacing:.06em; color:#b91c1c; }
.ws-stage-btn.is-on .ws-stage-letter { color:#991b1b; }
.ws-stage-title { font-size:.78rem; font-weight:700; color:#111827; line-height:1.15; }
.ws-stage-hint { font-size:.62rem; color:#6b7280; margin-top:.12rem; line-height:1.25; }
.ws-wizard-nav { display:flex; flex-wrap:wrap; gap:.5rem; align-items:center; justify-content:space-between; padding-top:.75rem; border-top:1px solid #e5e7eb; margin-top:1rem; }
.ws-wizard-panel { display:none; }
.ws-wizard-panel.is-on { display:block; }
.ws-filter-chip { border:1px solid #e5e7eb; background:#fff; border-radius:999px; font-size:11px; font-weight:600; padding:.2rem .65rem; cursor:pointer; color:#4b5563; }
.ws-filter-chip.is-on { background:#111827; color:#fff; border-color:#111827; }
.ws-confirm-needs { background:#fff7ed; color:#c2410c; border:1px solid #fdba74; }
.ws-confirm-ok { background:#ecfdf5; color:#047857; border:1px solid #a7f3d0; }
`;
  document.head.appendChild(style);
}

function statusLabel(s) {
  const map = {
    IN_PROGRESS: "In progress",
    RETURNED_TO_OPERATOR: "Returned to operator",
    APPROVED: "Approved",
    CANCELLED: "Cancelled",
  };
  return map[s] || s || "—";
}

function fmtDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

function fmtDateShort(v) {
  if (!v) return "—";
  try {
    const s = String(v);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString();
    }
    return new Date(v).toLocaleDateString();
  } catch {
    return String(v);
  }
}

function docAnalysisHref(docId, { page, q } = {}) {
  const base = `${window.location.pathname}${window.location.search}`;
  const params = new URLSearchParams();
  if (page) params.set("page", String(page));
  if (q) params.set("q", String(q).slice(0, 120));
  const qs = params.toString();
  return `${base}#/figaro/documents/${docId}${qs ? `?${qs}` : ""}`;
}

function docFileHref(apiUrl, docId) {
  return apiUrl(`/api/ai/documents/${docId}/file`);
}

const WIZARD_STAGES = [
  { id: "SETUP", letter: "A", title: "Setup", hint: "MSN · checks · MRO · uploads" },
  { id: "CLAUSES", letter: "B", title: "Clauses", hint: "Legal index + lease MR packages" },
  { id: "EFFECTIVITY", letter: "C", title: "Effectivity", hint: "MPD applicability conditions" },
  { id: "WORKSCOPE", letter: "D", title: "Workscope", hint: "Operator lines review" },
  { id: "MPD_GAPS", letter: "E", title: "MPD gaps", hint: "Eligible but missing on WS" },
  { id: "REPORT", letter: "F", title: "Report", hint: "Return or approve" },
];

const LEGACY_STAGE = {
  VALIDATION: "EFFECTIVITY",
  LEASE: "CLAUSES",
  LEGAL: "CLAUSES",
  DRAFT: "WORKSCOPE",
  DECISION: "REPORT",
  DONE: "DONE",
};

function normalizeStage(stage) {
  const s = String(stage || "SETUP").toUpperCase();
  return LEGACY_STAGE[s] || s;
}

function stageLabel(stage) {
  const n = normalizeStage(stage);
  const hit = WIZARD_STAGES.find((s) => s.id === n);
  if (n === "DONE") return "F Report";
  return hit ? `${hit.letter} ${hit.title}` : n || "A Setup";
}

function wizardIndex(stage) {
  const n = normalizeStage(stage);
  if (n === "DONE") return WIZARD_STAGES.length - 1;
  const i = WIZARD_STAGES.findIndex((s) => s.id === n);
  return i >= 0 ? i : 0;
}

function nextWizardStage(stage) {
  const i = wizardIndex(stage);
  return WIZARD_STAGES[Math.min(i + 1, WIZARD_STAGES.length - 1)].id;
}

function prevWizardStage(stage) {
  const i = wizardIndex(stage);
  return WIZARD_STAGES[Math.max(i - 1, 0)].id;
}

function legalDocsTable(el, docs, { apiUrl } = {}) {
  if (!docs?.length) {
    return el("div", {
      class: "text-xs text-gray-500",
      text: "No lease/legal documents yet — use + to upload.",
    });
  }
  return el("div", { class: "overflow-x-auto" }, [
    el("table", { class: "ws-table" }, [
      el("thead", {}, [
        el("tr", {}, [
          el("th", { text: "Kind" }),
          el("th", { text: "Name" }),
          el("th", { text: "Document date" }),
          el("th", { text: "Index / OCR" }),
          el("th", { text: "Open" }),
        ]),
      ]),
      el(
        "tbody",
        {},
        docs.map((d) => {
          const id = d.documentId || d.id;
          const name = d.title || d.originalFilename || `Document ${id}`;
          const kind = d.docType || d.role || "LEGAL";
          const date = d.documentDate || d.date || null;
          const ocr =
            d.ocrStatus ||
            (d.ocrQuality != null ? `Q ${Math.round(Number(d.ocrQuality) * 100)}%` : null);
          const indexBits = [
            d.status || null,
            ocr,
            d.chunkCount != null ? `${d.chunkCount} chunks` : null,
            d.superseding ? "superseding" : null,
          ]
            .filter(Boolean)
            .join(" · ");
          return el("tr", {}, [
            el("td", {
              class: "text-[10px] uppercase text-gray-500 font-semibold whitespace-nowrap",
              text: kind,
            }),
            el("td", { class: "text-sm", text: name }),
            el("td", { class: "text-xs text-gray-500 whitespace-nowrap", text: fmtDateShort(date) }),
            el("td", {}, [
              el("span", {
                class: `text-[10px] font-semibold rounded px-1.5 py-0.5 ${
                  String(d.status || "").toUpperCase() === "INDEXED"
                    ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                    : "bg-amber-50 text-amber-900 border border-amber-200"
                }`,
                text: indexBits || "—",
              }),
            ]),
            el("td", {}, [
              el("div", { class: "flex gap-1" }, [
                el("a", {
                  href: docAnalysisHref(id),
                  target: "_blank",
                  rel: "noopener",
                  class: "ws-icon-btn",
                  title: "Open analysis",
                  text: "↗",
                }),
                apiUrl
                  ? el("a", {
                      href: docFileHref(apiUrl, id),
                      target: "_blank",
                      rel: "noopener",
                      class: "ws-icon-btn",
                      title: "Open file",
                      text: "⇩",
                    })
                  : null,
              ]),
            ]),
          ]);
        })
      ),
    ]),
  ]);
}

function fileDropzone(el, { label, accept, multiple = false, onFiles, inputRef }) {
  const input = el("input", {
    type: "file",
    accept,
    multiple: multiple || undefined,
    class: "hidden",
  });
  if (inputRef) inputRef.current = input;
  const zone = el("div", {
    class: "ws-drop",
    text: label,
    onclick: () => input.click(),
  });
  zone.addEventListener("dragover", (e) => {
    e.preventDefault();
    zone.classList.add("is-drag");
  });
  zone.addEventListener("dragleave", () => zone.classList.remove("is-drag"));
  zone.addEventListener("drop", (e) => {
    e.preventDefault();
    zone.classList.remove("is-drag");
    const files = [...(e.dataTransfer?.files || [])];
    if (!files.length) return;
    if (onFiles) onFiles(files, input);
    else {
      const dt = new DataTransfer();
      for (const f of multiple ? files : files.slice(0, 1)) dt.items.add(f);
      input.files = dt.files;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }
  });
  input.addEventListener("change", () => {
    if (input.files?.length) {
      zone.textContent = multiple
        ? `${input.files.length} file(s) selected`
        : input.files[0].name;
    }
  });
  return el("div", { class: "space-y-1" }, [zone, input]);
}

function plusUploadBtn(el, { title = "Upload", onClick }) {
  return el("button", {
    type: "button",
    class: "ws-plus",
    title,
    text: "+",
    onclick: onClick,
  });
}

function workscopesChrome(el, { title, crumbs = [], actions = null }) {
  return el("div", { class: "space-y-2" }, [
    el("div", { class: "flex flex-wrap items-center gap-3" }, [
      el("a", { href: "#/figaro", class: "text-sm text-dae hover:underline", text: "Figaro" }),
      ...crumbs.map((c) =>
        el("span", { class: "flex items-center gap-3" }, [
          el("span", { class: "text-gray-300", text: "/" }),
          c.href
            ? el("a", { href: c.href, class: "text-sm text-dae hover:underline", text: c.label })
            : el("span", { class: "text-sm text-gray-500", text: c.label }),
        ])
      ),
    ]),
    el("div", { class: "flex flex-wrap items-start justify-between gap-3" }, [
      el("h1", { class: "text-xl font-semibold text-gray-900 tracking-tight", text: title }),
      actions,
    ]),
  ]);
}

function mpdNavActions(el) {
  return el("div", { class: "flex flex-wrap gap-2" }, [
    el("a", {
      href: "#/figaro/workscopes",
      class: "inline-flex items-center rounded-lg border border-gray-200 bg-white text-sm px-3 py-1.5 text-gray-800 hover:bg-gray-50",
      text: "Evaluations",
    }),
    el("a", {
      href: "#/figaro/workscopes/mpds",
      class: "inline-flex items-center rounded-lg border border-dae/30 bg-red-50 text-sm font-medium px-3 py-1.5 text-dae hover:bg-red-100",
      text: "MPD library",
    }),
    el("a", {
      href: "#/figaro/workscopes/new",
      class: "inline-flex items-center rounded-lg bg-dae text-white text-sm font-medium px-3 py-1.5 hover:opacity-90",
      text: "New evaluation",
    }),
  ]);
}

/** Evaluation-relevant columns for confirming an imported MPD. */
function mpdTasksTable(el, tasks, { emptyText = "No tasks." } = {}) {
  if (!tasks?.length) {
    return el("div", { class: "px-4 py-6 text-sm text-gray-500", text: emptyText });
  }
  return el("div", { class: "overflow-x-auto max-h-[32rem]" }, [
    el("table", { class: "ws-table" }, [
      el("thead", {}, [
        el("tr", {}, [
          el("th", { text: "MPD task #" }),
          el("th", { text: "Title / description" }),
          el("th", { text: "Interval" }),
          el("th", { text: "Threshold" }),
          el("th", { text: "Man-hours" }),
          el("th", { text: "Effectivity" }),
          el("th", { text: "Zone" }),
          el("th", { text: "Task code" }),
        ]),
      ]),
      el(
        "tbody",
        {},
        tasks.map((t) =>
          el("tr", {}, [
            el("td", { class: "font-mono text-xs whitespace-nowrap", text: t.taskNumber || "—" }),
            el("td", {
              class: "text-xs max-w-md",
              text: (t.taskTitle || t.description || "—").slice(0, 160),
              title: t.description || t.taskTitle || "",
            }),
            el("td", {
              class: "text-xs whitespace-nowrap",
              text: t.intervalDisplay || t.intervalRaw || "—",
              title: t.intervalRaw || "",
            }),
            el("td", { class: "text-xs whitespace-nowrap", text: t.thresholdRaw || "—" }),
            el("td", {
              class: "text-xs",
              text: t.manHours != null && t.manHours !== "" ? String(t.manHours) : "—",
            }),
            el("td", {
              class: "text-xs max-w-[14rem]",
              title: t.effectivity || t.effectivityRules?.display || "",
              text: (
                t.effectivityRules?.display ||
                t.effectivity ||
                "ALL"
              ).slice(0, 80),
            }),
            el("td", { class: "text-xs", text: (t.zone || "—").toString().slice(0, 40) }),
            el("td", { class: "text-xs", text: t.taskType || t.skillCode || "—" }),
          ])
        )
      ),
    ]),
  ]);
}

function statusPill(el, status) {
  return el("span", {
    class: `ws-status ws-status-${status || "IN_PROGRESS"}`,
    text: statusLabel(status),
  });
}

function revBadge(el, revisionNumber) {
  if (!revisionNumber) return el("span", { class: "text-xs text-amber-700", text: "Revision not set" });
  return el("span", { class: "ws-rev", text: `Rev ${revisionNumber}` });
}

export function renderWorkscopesRoute({ el, fetchJson, apiUrl, cleanText, routeKey }) {
  ensureStyles();
  const raw = String(routeKey || "").replace(/^figaro\/workscopes\/?/, "").split("?")[0];
  const parts = raw.split("/").filter(Boolean);
  const host = el("div", { class: "space-y-4" });

  if (parts[0] === "mpds") {
    if (parts[1]) renderMpdDetail({ el, fetchJson, apiUrl, cleanText, host, id: Number(parts[1]) });
    else renderMpdLibrary({ el, fetchJson, apiUrl, cleanText, host });
  } else if (parts[0] && /^\d+$/.test(parts[0])) {
    renderEvaluationDetail({ el, fetchJson, apiUrl, cleanText, host, id: Number(parts[0]) });
  } else if (parts[0] === "new") {
    renderNewEvaluation({ el, fetchJson, apiUrl, cleanText, host });
  } else {
    renderDashboard({ el, fetchJson, apiUrl, cleanText, host });
  }
  return host;
}

function renderDashboard({ el, fetchJson, apiUrl, cleanText, host }) {
  host.replaceChildren(el("div", { class: "text-sm text-gray-500", text: "Loading workscopes…" }));

  (async () => {
    try {
      const list = await fetchJson(apiUrl("/api/ai/workscopes"));
      const evaluations = list.evaluations || [];
      const byStatus = {
        IN_PROGRESS: evaluations.filter((e) => e.status === "IN_PROGRESS").length,
        RETURNED_TO_OPERATOR: evaluations.filter((e) => e.status === "RETURNED_TO_OPERATOR").length,
        APPROVED: evaluations.filter((e) => e.status === "APPROVED").length,
      };

      host.replaceChildren(
        el("div", { class: "space-y-5" }, [
          workscopesChrome(el, { title: "Workscopes", actions: mpdNavActions(el) }),
          el("div", { class: "grid gap-3 sm:grid-cols-3" }, [
            statCard(el, "In progress", byStatus.IN_PROGRESS, "orange"),
            statCard(el, "Returned to operator", byStatus.RETURNED_TO_OPERATOR, "blue"),
            statCard(el, "Approved", byStatus.APPROVED, "green"),
          ]),
          el("div", { class: "ws-card overflow-hidden" }, [
            el("div", { class: "px-4 py-3 border-b border-gray-100 flex items-center justify-between" }, [
              el("div", { class: "text-sm font-semibold text-gray-900", text: "Evaluation history" }),
              el("a", {
                href: "#/figaro/workscopes/new",
                class: "text-sm text-dae hover:underline font-medium",
                text: "New evaluation →",
              }),
            ]),
            evaluations.length
              ? el("div", { class: "overflow-x-auto" }, [
                  el("table", { class: "ws-table" }, [
                    el("thead", {}, [
                      el("tr", {}, [
                        el("th", { text: "Evaluation" }),
                        el("th", { text: "Stage" }),
                        el("th", { text: "Operator" }),
                        el("th", { text: "MSN" }),
                        el("th", { text: "Check" }),
                        el("th", { text: "MPD rev" }),
                        el("th", { text: "Status" }),
                        el("th", { text: "Updated" }),
                        el("th", { text: "" }),
                      ]),
                    ]),
                    el(
                      "tbody",
                      {},
                      evaluations.map((e) =>
                        el("tr", {}, [
                          el("td", {}, [
                            el("a", {
                              href: `#/figaro/workscopes/${e.id}`,
                              class: "text-dae hover:underline font-medium",
                              text: e.title || `Evaluation #${e.id}`,
                            }),
                          ]),
                          el("td", { class: "text-xs", text: stageLabel(e.stage) }),
                          el("td", { text: e.operatorName || "—" }),
                          el("td", { class: "font-mono text-xs", text: e.msn || "—" }),
                          el("td", { text: e.checkType || "—" }),
                          el("td", {}, [
                            e.mpdRevisionId
                              ? el("a", { href: `#/figaro/workscopes/mpds/${e.mpdRevisionId}`, class: "inline-flex" }, [
                                  revBadge(el, e.mpdRevisionNumber),
                                ])
                              : revBadge(el, e.mpdRevisionNumber),
                          ]),
                          el("td", {}, [statusPill(el, e.status)]),
                          el("td", { class: "text-xs text-gray-500", text: fmtDate(e.updatedAt) }),
                          el("td", { class: "text-right" }, [
                            el("button", {
                              type: "button",
                              class: "ws-icon-btn ws-icon-btn-danger",
                              title: "Delete evaluation",
                              text: "×",
                              onclick: async (evt) => {
                                evt.preventDefault();
                                evt.stopPropagation();
                                if (
                                  !confirm(
                                    `Delete “${e.title || `#${e.id}`}”? Cascades claim uploads for this evaluation. MPD library is kept.`
                                  )
                                )
                                  return;
                                try {
                                  await fetchJson(apiUrl(`/api/ai/workscopes/${e.id}`), { method: "DELETE" });
                                  // re-render list
                                  renderDashboard({ el, fetchJson, apiUrl, cleanText, host });
                                } catch (err) {
                                  alert(cleanText(err?.message) || String(err));
                                }
                              },
                            }),
                          ]),
                        ])
                      )
                    ),
                  ]),
                ])
              : el("div", {
                  class: "px-4 py-8 text-sm text-gray-500",
                  text: "No evaluations yet.",
                }),
          ]),
        ])
      );
    } catch (e) {
      host.replaceChildren(
        el("div", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) })
      );
    }
  })();
}

function statCard(el, label, value, tone) {
  const tones = {
    orange: "border-orange-200 bg-orange-50/40",
    blue: "border-blue-200 bg-blue-50/40",
    green: "border-emerald-200 bg-emerald-50/40",
  };
  return el("div", { class: `rounded-xl border p-4 ${tones[tone] || "border-gray-200"}` }, [
    el("div", { class: "text-xs uppercase tracking-wide text-gray-500 font-semibold", text: label }),
    el("div", { class: "text-2xl font-semibold text-gray-900 mt-1", text: String(value) }),
  ]);
}

function renderMpdLibrary({ el, fetchJson, apiUrl, cleanText, host }) {
  host.replaceChildren(el("div", { class: "text-sm text-gray-500", text: "Loading MPD library…" }));

  async function paint() {
    const data = await fetchJson(apiUrl("/api/ai/mpd/revisions"));
    const revisions = data.revisions || [];

    const oemSelect = el("select", { class: "border border-gray-200 rounded-lg px-2 py-2 text-sm" }, [
      el("option", { value: "", text: "Detect OEM from filename" }),
      el("option", { value: "AIRBUS", text: "Airbus" }),
      el("option", { value: "BOEING", text: "Boeing" }),
      el("option", { value: "ATR", text: "ATR" }),
    ]);
    const fleetSelect = el("select", { class: "border border-gray-200 rounded-lg px-2 py-2 text-sm" }, [
      el("option", { value: "", text: "Fleet: detect from file" }),
      el("option", { value: "B737NG", text: "Boeing 737 NG" }),
      el("option", { value: "B737MAX", text: "Boeing 737 MAX" }),
      el("option", { value: "B737", text: "Boeing 737 combined (NG+MAX)" }),
      el("option", { value: "A320", text: "Airbus A320 family" }),
      el("option", { value: "ATR72", text: "ATR 72" }),
      el("option", { value: "ATR42", text: "ATR 42" }),
    ]);
    const revInput = el("input", {
      class: "border border-gray-200 rounded-lg px-3 py-2 text-sm w-36",
      placeholder: "Rev e.g. R50",
    });
    const fileInput = el("input", { type: "file", accept: ".xls,.xlsx", class: "text-sm" });
    const statusEl = el("div", { class: "text-sm text-gray-600" });
    const uploadBtn = el(
      "button",
      {
        type: "button",
        class: "rounded-lg bg-dae text-white text-sm font-medium px-3 py-2",
        onclick: async () => {
          const file = fileInput.files?.[0];
          if (!file) {
            statusEl.textContent = "Choose an MPD Excel file first (.xls / .xlsx).";
            statusEl.className = "text-sm text-red-700";
            return;
          }
          statusEl.className = "text-sm text-gray-600";
          statusEl.textContent = "Importing & preprocessing — large MPDs can take a minute…";
          try {
            const fd = new FormData();
            fd.append("file", file);
            if (oemSelect.value) fd.append("oem", oemSelect.value);
            if (fleetSelect.value) fd.append("fleetType", fleetSelect.value);
            if (revInput.value.trim()) fd.append("revisionNumber", revInput.value.trim());
            const res = await fetch(apiUrl("/api/ai/mpd/import"), {
              method: "POST",
              body: fd,
              credentials: "include",
            });
            const json = await res.json();
            if (!json.ok) throw new Error(json.error || "Import failed");
            statusEl.className = "text-sm text-emerald-700";
            statusEl.textContent = `Imported ${json.taskCount} tasks — ${json.oem || ""} ${json.revisionNumber || ""}. Opening task table…`;
            location.hash = `#/figaro/workscopes/mpds/${json.revisionId}`;
          } catch (err) {
            statusEl.textContent = cleanText(err?.message) || String(err);
            statusEl.className = "text-sm text-red-700";
          }
        },
      },
      ["Upload & preprocess"]
    );

    host.replaceChildren(
      el("div", { class: "space-y-5" }, [
        workscopesChrome(el, {
          title: "MPD library",
          crumbs: [{ href: "#/figaro/workscopes", label: "Workscopes" }],
          actions: mpdNavActions(el),
        }),
        el("p", {
          class: "text-sm text-gray-600 max-w-3xl",
          text:
            "Upload OEM MPD Excels here. For Boeing, set fleet to NG, MAX, or combined (NG+MAX) — many mpdsup files cover both. You can edit fleet type after import.",
        }),
        el("div", { class: "ws-card p-4 space-y-3" }, [
          el("div", { class: "text-sm font-semibold text-gray-900", text: "Upload OEM MPD (.xls / .xlsx)" }),
          el("p", {
            class: "text-xs text-gray-500",
            text: "Airbus A320 family, Boeing 737 NG / MAX mpdsup, ATR MPD72 / MRBR. Confirm revision (R50, R37, SEP25…).",
          }),
          el("div", { class: "flex flex-wrap items-center gap-2" }, [
            fileInput,
            oemSelect,
            fleetSelect,
            revInput,
            uploadBtn,
          ]),
          statusEl,
        ]),
        el("div", { class: "ws-card overflow-hidden" }, [
          el("div", {
            class: "px-4 py-3 border-b border-gray-100 text-sm font-semibold",
            text: "Revisions on this server",
          }),
          revisions.length
            ? el("table", { class: "ws-table" }, [
                el("thead", {}, [
                  el("tr", {}, [
                    el("th", { text: "Title" }),
                    el("th", { text: "OEM" }),
                    el("th", { text: "Fleet" }),
                    el("th", { text: "Revision" }),
                    el("th", { text: "Tasks" }),
                    el("th", { text: "Status" }),
                    el("th", { text: "Uploaded" }),
                  ]),
                ]),
                el(
                  "tbody",
                  {},
                  revisions.map((r) =>
                    el("tr", {}, [
                      el("td", {}, [
                        el("a", {
                          href: `#/figaro/workscopes/mpds/${r.id}`,
                          class: "text-dae hover:underline font-medium",
                          text: r.title || `${r.oem} ${r.fleetType}`,
                        }),
                        r.isSupplement
                          ? el("span", {
                              class: "ml-2 text-[10px] uppercase tracking-wide text-amber-700",
                              text: "Supplement",
                            })
                          : null,
                      ]),
                      el("td", { text: r.oem || "—" }),
                      el("td", { text: r.fleetType || "—" }),
                      el("td", {}, [revBadge(el, r.revisionNumber)]),
                      el("td", { text: String(r.taskCount ?? "—") }),
                      el("td", { text: r.status }),
                      el("td", { class: "text-xs text-gray-500", text: fmtDate(r.createdAt) }),
                    ])
                  )
                ),
              ])
            : el("div", { class: "ws-callout m-4" }, [
                el("div", { class: "text-sm font-semibold text-amber-900", text: "Nothing uploaded yet" }),
                el("p", {
                  class: "text-sm text-amber-900/90 mt-1",
                  text:
                    "Use the upload box with OEM MPD Excels. After import you get a full task table to confirm intervals, hours, and effectivity.",
                }),
              ]),
        ]),
      ])
    );
  }

  paint().catch((e) => {
    host.replaceChildren(
      el("div", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) })
    );
  });
}

function renderMpdDetail({ el, fetchJson, apiUrl, cleanText, host, id }) {
  host.replaceChildren(el("div", { class: "text-sm text-gray-500", text: "Loading revision…" }));
  const qInput = el("input", {
    class: "border border-gray-200 rounded-lg px-3 py-2 text-sm w-72",
    placeholder: "Filter task #, title, interval…",
  });

  async function paint(q = "") {
    const [data, docsRes] = await Promise.all([
      fetchJson(apiUrl(`/api/ai/mpd/revisions/${id}?limit=500&q=${encodeURIComponent(q)}`)),
      fetchJson(apiUrl(`/api/ai/mpd/revisions/${id}/documents`)).catch(() => ({ documents: [] })),
    ]);
    const r = data.revision;
    if (!r) throw new Error("Revision not found");
    const attached = docsRes.documents || [];

    const fleetOpts = [
      ["B737NG", "B737NG — 737-700/800/900"],
      ["B737MAX", "B737MAX — 737-8/9/10/8200"],
      ["B737", "B737 — combined NG+MAX"],
      ["A320", "A320 family"],
      ["ATR72", "ATR72"],
      ["ATR42", "ATR42"],
    ];
    const fleetSelect = el(
      "select",
      { class: "border border-gray-200 rounded-lg px-2 py-1.5 text-sm" },
      [
        ...fleetOpts.map(([v, label]) => el("option", { value: v, text: label, selected: r.fleetType === v })),
        ...(r.fleetType && !fleetOpts.some(([v]) => v === r.fleetType)
          ? [el("option", { value: r.fleetType, text: r.fleetType, selected: true })]
          : []),
      ]
    );

    const revInput = el("input", {
      class: "border border-gray-200 rounded-lg px-2 py-1.5 text-sm font-mono w-28",
      value: r.revisionNumber || "",
    });
    const titleInput = el("input", {
      class: "border border-gray-200 rounded-lg px-2 py-1.5 text-sm w-72",
      value: r.title || "",
    });
    const saveMsg = el("span", { class: "text-xs text-gray-500" });
    const notesFile = el("input", { type: "file", accept: ".pdf,.xls,.xlsx", class: "text-sm" });
    const roleSelect = el(
      "select",
      { class: "border border-gray-200 rounded-lg px-2 py-1.5 text-sm" },
      [
        el("option", { value: "NOTES", text: "NOTES / MPD note PDF", selected: true }),
        el("option", { value: "SUPPLEMENT", text: "SUPPLEMENT" }),
        el("option", { value: "OTHER", text: "OTHER" }),
      ]
    );

    host.replaceChildren(
      el("div", { class: "space-y-4" }, [
        workscopesChrome(el, {
          title: r.title || `${r.oem} ${r.fleetType}`,
          crumbs: [
            { href: "#/figaro/workscopes", label: "Workscopes" },
            { href: "#/figaro/workscopes/mpds", label: "MPD library" },
          ],
          actions: mpdNavActions(el),
        }),
        el("div", { class: "ws-card p-4 space-y-3" }, [
          el("div", { class: "text-sm font-semibold text-gray-900", text: "Revision identity (editable)" }),
          el("div", { class: "flex flex-wrap items-end gap-3" }, [
            el("label", { class: "space-y-1" }, [
              el("div", { class: "text-[11px] uppercase text-gray-500 font-semibold", text: "Revision" }),
              revInput,
            ]),
            el("label", { class: "space-y-1" }, [
              el("div", { class: "text-[11px] uppercase text-gray-500 font-semibold", text: "Fleet type" }),
              fleetSelect,
            ]),
            el("label", { class: "space-y-1 flex-1 min-w-[12rem]" }, [
              el("div", { class: "text-[11px] uppercase text-gray-500 font-semibold", text: "Title" }),
              titleInput,
            ]),
            el(
              "button",
              {
                type: "button",
                class: "rounded-lg bg-dae text-white text-sm font-medium px-3 py-2",
                onclick: async () => {
                  saveMsg.textContent = "Saving…";
                  saveMsg.className = "text-xs text-gray-500";
                  try {
                    await fetchJson(apiUrl(`/api/ai/mpd/revisions/${id}`), {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        fleetType: fleetSelect.value,
                        revisionNumber: revInput.value.trim(),
                        title: titleInput.value.trim(),
                      }),
                    });
                    saveMsg.textContent = "Saved.";
                    await paint(qInput.value.trim());
                  } catch (err) {
                    saveMsg.textContent = cleanText(err?.message) || String(err);
                    saveMsg.className = "text-xs text-red-700";
                  }
                },
              },
              ["Save"]
            ),
            saveMsg,
          ]),
          el("div", { class: "flex flex-wrap items-center gap-3 text-sm text-gray-600" }, [
            revBadge(el, r.revisionNumber),
            el("span", { text: `${r.oem} · ${r.fleetType || ""}` }),
            el("span", { class: "text-gray-500", text: `${r.taskCount || 0} tasks` }),
            r.importReport?.note
              ? el("span", { class: "text-xs text-amber-800", text: r.importReport.note })
              : null,
          ]),
          el("div", { class: "flex flex-wrap gap-2 pt-1" }, [
            el(
              "button",
              {
                type: "button",
                class: "rounded-lg border border-gray-200 px-3 py-1.5 text-sm",
                onclick: async () => {
                  saveMsg.textContent = "Reparsing effectivity & intervals…";
                  try {
                    const out = await fetchJson(apiUrl(`/api/ai/mpd/revisions/${id}/reparse`), {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: "{}",
                    });
                    saveMsg.textContent = `Reparsed ${out.updated ?? "?"} tasks.`;
                    await paint(qInput.value.trim());
                  } catch (err) {
                    saveMsg.textContent = cleanText(err?.message) || String(err);
                    saveMsg.className = "text-xs text-red-700";
                  }
                },
              },
              ["Reparse tasks"]
            ),
            el(
              "button",
              {
                type: "button",
                class: "rounded-lg border border-red-200 text-red-800 px-3 py-1.5 text-sm",
                onclick: async () => {
                  if (!confirm("Delete this MPD revision and all its tasks?")) return;
                  try {
                    const res = await fetch(apiUrl(`/api/ai/mpd/revisions/${id}`), {
                      method: "DELETE",
                      credentials: "include",
                      headers: { Accept: "application/json" },
                    });
                    const out = await res.json().catch(() => ({}));
                    if (res.status === 409 || out.blocked) {
                      if (
                        !confirm(
                          `${out.message || "Revision is in use."}\n\nForce-delete and detach ${
                            out.evaluations?.length || 0
                          } evaluation(s)?`
                        )
                      )
                        return;
                      await fetchJson(apiUrl(`/api/ai/mpd/revisions/${id}?force=1`), {
                        method: "DELETE",
                      });
                    } else if (!res.ok || out.ok === false) {
                      throw new Error(out.error || out.message || `HTTP ${res.status}`);
                    }
                    location.hash = "#/figaro/workscopes/mpds";
                  } catch (err) {
                    saveMsg.textContent = cleanText(err?.message) || String(err);
                    saveMsg.className = "text-xs text-red-700";
                  }
                },
              },
              ["Delete revision"]
            ),
          ]),
        ]),
        el("div", { class: "ws-card p-4 space-y-3" }, [
          el("div", { class: "text-sm font-semibold", text: "Supplement / notes PDFs" }),
          el("p", {
            class: "text-xs text-gray-500",
            text:
              "Attach MPD NOTE or supplement PDFs for this revision. Effectivity lines that reference NOTE will show a note ref; extraction into tasks is parametrized later.",
          }),
          attached.length
            ? el(
                "ul",
                { class: "text-sm space-y-1" },
                attached.map((d) =>
                  el("li", {}, [
                    el("span", {
                      class: "text-[10px] uppercase text-gray-400 mr-2",
                      text: d.role || "NOTES",
                    }),
                    el("a", {
                      href: `#/figaro/documents/${d.documentId}`,
                      class: "text-dae hover:underline",
                      text: d.title || d.originalFilename || `Document ${d.documentId}`,
                    }),
                  ])
                )
              )
            : el("div", { class: "text-xs text-gray-500", text: "No supplement/notes files yet." }),
          el("div", { class: "flex flex-wrap items-end gap-2" }, [
            roleSelect,
            notesFile,
            el(
              "button",
              {
                type: "button",
                class: "rounded-lg bg-dae text-white text-sm px-3 py-2",
                onclick: async () => {
                  if (!notesFile.files?.[0]) {
                    saveMsg.textContent = "Choose a file to attach.";
                    return;
                  }
                  const fd = new FormData();
                  fd.append("file", notesFile.files[0]);
                  fd.append("role", roleSelect.value);
                  saveMsg.textContent = "Uploading…";
                  try {
                    const res = await fetch(apiUrl(`/api/ai/mpd/revisions/${id}/documents`), {
                      method: "POST",
                      body: fd,
                      credentials: "include",
                    });
                    const json = await res.json();
                    if (!json.ok) throw new Error(json.error || "Upload failed");
                    saveMsg.textContent = "Attached.";
                    await paint(qInput.value.trim());
                  } catch (err) {
                    saveMsg.textContent = cleanText(err?.message) || String(err);
                    saveMsg.className = "text-xs text-red-700";
                  }
                },
              },
              ["Attach file"]
            ),
          ]),
        ]),
        el("p", {
          class: "text-xs text-gray-500 max-w-3xl",
          text:
            "Confirm before use: task #, standardized interval (FH/FC/MO/DY + YE/YR/C/A), threshold, man-hours, effectivity. ALL+NOTE never becomes a question.",
        }),
        el("div", { class: "flex flex-wrap gap-2 items-center" }, [
          qInput,
          el(
            "button",
            {
              type: "button",
              class: "rounded-lg border border-gray-200 px-3 py-2 text-sm",
              onclick: () => void paint(qInput.value.trim()),
            },
            ["Search"]
          ),
          el("a", {
            href: `#/figaro/workscopes/new?mpd=${id}`,
            class: "rounded-lg bg-dae text-white text-sm font-medium px-3 py-2",
            text: "Use on new evaluation",
          }),
        ]),
        el("div", { class: "ws-card overflow-hidden" }, [
          mpdTasksTable(el, r.tasks || [], {
            emptyText: q ? "No tasks match that filter." : "No tasks stored for this revision.",
          }),
        ]),
      ])
    );
  }

  paint().catch((e) => {
    host.replaceChildren(
      el("div", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) })
    );
  });
}

function assetLabel(a) {
  const msn = a.serialNumber || a.msn || "";
  const reg = a.metadata?.registration || a.registration || "";
  const model = a.model || "";
  const op = a.operatorName || "";
  return [msn && `MSN ${msn}`, reg, model, op].filter(Boolean).join(" · ");
}

function renderNewEvaluation({ el, fetchJson, apiUrl, cleanText, host }) {
  host.replaceChildren(el("div", { class: "text-sm text-gray-500", text: "Loading setup…" }));

  (async () => {
    try {
      const hash = String(window.location.hash || "");
      const qs = new URLSearchParams(hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "");
      const preselectMpd = qs.get("mpd");

      const [mpds, scope] = await Promise.all([
        fetchJson(apiUrl("/api/ai/mpd/revisions")),
        fetchJson(apiUrl("/api/ai/scope-options")).catch(() => ({ operators: [], assets: [] })),
      ]);
      const revisions = mpds.revisions || [];
      const assets = (scope.assets || [])
        .filter((a) => String(a.assetType || "AIRFRAME") === "AIRFRAME")
        .slice()
        .sort((a, b) => String(a.serialNumber || "").localeCompare(String(b.serialNumber || "")));
      let legalDocs = [];

      async function fetchLegalLibrary({ assetId = null, operatorId = null } = {}) {
        const qs = new URLSearchParams();
        qs.set("legalOnly", "1");
        if (assetId) qs.set("contextAssetId", String(assetId));
        else if (operatorId) qs.set("contextOperatorId", String(operatorId));
        // Scoped fetch so operator/MSN leases are not lost to global LIMIT 500 ordering.
        const data = await fetchJson(apiUrl(`/api/ai/documents?${qs}`)).catch(() => ({ documents: [] }));
        return (data.documents || []).filter((d) =>
          ["LEASE", "LEASE_AMENDMENT", "OTHER_LEGAL"].includes(String(d.docType || "").toUpperCase())
        );
      }

      try {
        legalDocs = await fetchLegalLibrary();
      } catch {
        legalDocs = [];
      }

      let selectedAsset = null;
      let selectedFleet = { oem: null, fleetFamily: null, label: null };
      let lastExtract = null;

      const titleIn = el("input", {
        class: "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm",
        placeholder: "Title for the check seeking approval",
      });
      const checkIn = el("input", {
        class: "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm",
        placeholder: "Check type e.g. 9YR / 2C / 12YE",
      });
      const notesIn = el("textarea", {
        class: "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm min-h-[3.5rem]",
        placeholder: "Notes…",
      });
      const inductionIn = el("input", {
        type: "date",
        class: "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm",
      });
      const crsIn = el("input", {
        type: "date",
        class: "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm",
      });
      const errEl = el("div", { class: "text-sm text-red-700" });
      const fleetHint = el("div", { class: "text-sm text-gray-600" });
      const coverageHost = el("div", { class: "text-sm text-gray-600" });
      const mpdSelect = el("select", { class: "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" });
      const msnFilter = el("input", {
        class: "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono",
        placeholder: "Filter MSN / registration…",
      });
      const msnSelect = el("select", { class: "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm", size: 7 });
      const wsFileRef = { current: null };
      const supportFileRef = { current: null };
      const ldndFileRef = { current: null };
      const legalFileRef = { current: null };
      const legalTypeSelect = el(
        "select",
        { class: "border border-gray-200 rounded-lg px-2 py-1.5 text-sm", title: "Override only if system kind is wrong" },
        [
          el("option", { value: "AUTO", text: "Kind: auto (system)" }),
          el("option", { value: "LEASE", text: "Override: Lease" }),
          el("option", { value: "LEASE_AMENDMENT", text: "Override: Amendment" }),
          el("option", { value: "OTHER_LEGAL", text: "Override: Other legal" }),
        ]
      );

      function fillMsnOptions(filter = "") {
        const q = filter.trim().toLowerCase();
        const filtered = !q
          ? assets.slice(0, 400)
          : assets.filter((a) => assetLabel(a).toLowerCase().includes(q)).slice(0, 400);
        msnSelect.replaceChildren(
          el("option", { value: "", text: filtered.length ? "Select MSN…" : "No MSN match" }),
          ...filtered.map((a) => el("option", { value: String(a.id), text: assetLabel(a) }))
        );
      }

      function fillMpdOptions() {
        const matched = revisions.filter((r) => revisionMatchesFleet(r, selectedFleet));
        const list = matched.length ? matched : revisions;
        mpdSelect.replaceChildren(
          el("option", {
            value: "",
            text: list.length ? "Confirm MPD revision…" : "No MPD revisions — open MPD library",
          }),
          ...list.map((r) =>
            el("option", {
              value: String(r.id),
              text: `${r.oem} ${r.fleetType || ""} — Rev ${r.revisionNumber} (${r.taskCount || 0} tasks)`,
              selected: preselectMpd && String(r.id) === String(preselectMpd),
            })
          )
        );
        if (selectedFleet.label) {
          fleetHint.textContent = `Aircraft: ${selectedFleet.label}. Matching MPD revisions: ${matched.length}/${revisions.length}.`;
        } else {
          fleetHint.textContent = `${revisions.length} MPD revision(s) on server.`;
        }
        if (preselectMpd) mpdSelect.value = String(preselectMpd);
      }

      msnFilter.addEventListener("input", () => fillMsnOptions(msnFilter.value));
      msnSelect.addEventListener("change", async () => {
        selectedAsset = assets.find((a) => String(a.id) === msnSelect.value) || null;
        selectedFleet = guessFleetFromModel(selectedAsset?.model);
        if (selectedAsset && (!titleIn.value.trim() || titleIn.dataset.autofill === "1")) {
          titleIn.value = [selectedAsset.operatorName, selectedAsset.serialNumber && `MSN ${selectedAsset.serialNumber}`, checkIn.value.trim()]
            .filter(Boolean)
            .join(" · ");
          titleIn.dataset.autofill = "1";
        }
        fillMpdOptions();
        legalHost.replaceChildren(el("div", { class: "text-xs text-gray-500", text: "Loading legal library…" }));
        try {
          legalDocs = await fetchLegalLibrary({
            assetId: selectedAsset?.id || null,
            operatorId: selectedAsset?.operatorId || null,
          });
        } catch {
          /* keep prior */
        }
        paintLegal();
      });
      checkIn.addEventListener("input", () => {
        if (selectedAsset && titleIn.dataset.autofill === "1") {
          titleIn.value = [selectedAsset.operatorName, selectedAsset.serialNumber && `MSN ${selectedAsset.serialNumber}`, checkIn.value.trim()]
            .filter(Boolean)
            .join(" · ");
        }
      });
      titleIn.addEventListener("input", () => {
        titleIn.dataset.autofill = "0";
      });

      const legalHost = el("div", { class: "space-y-1 text-sm" });
      const mroLocationIn = el("input", {
        class: "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm",
        placeholder: "e.g. HAECO Xiamen · SASCO Singapore · operator hangar FRA",
      });

      function sameId(a, b) {
        if (a == null || b == null) return false;
        return Number(a) === Number(b);
      }
      function docMatchesAsset(d, asset) {
        if (!asset) return true;
        if (sameId(d.assetId, asset.id)) return true;
        if (sameId(d.operatorId, asset.operatorId)) return true;
        const msn = String(asset.serialNumber || asset.msn || "").trim();
        if (msn) {
          const hay = `${d.title || ""} ${d.originalFilename || ""} ${d.assetSerial || ""}`.toLowerCase();
          if (hay.includes(msn.toLowerCase())) return true;
        }
        return false;
      }

      function paintLegal() {
        const related = (selectedAsset ? legalDocs.filter((d) => docMatchesAsset(d, selectedAsset)) : legalDocs)
          .slice()
          .sort((a, b) => {
            const da = new Date(a.documentDate || a.createdAt || 0).getTime();
            const db = new Date(b.documentDate || b.createdAt || 0).getTime();
            return db - da;
          })
          .slice(0, 80);
        if (!related.length) {
          legalHost.replaceChildren(
            el("div", {
              class: "text-xs text-gray-500",
              text: selectedAsset
                ? `No legal docs matched MSN ${selectedAsset.serialNumber || "—"} (${legalDocs.length} legal in library scope). Upload with + or open Figaro → Documents.`
                : "No lease/legal documents yet — use + to upload, or select an MSN to pull its library.",
            })
          );
          return;
        }
        legalHost.replaceChildren(
          el("div", {
            class: "text-[11px] text-gray-500 mb-1",
            text: selectedAsset
              ? `${related.length} legal doc(s) for MSN ${selectedAsset.serialNumber} — all will be linked (no multi-select). Kind is system-identified; INDEXED/OCR shown as badges.`
              : `${related.length} legal doc(s) — select an MSN to filter to that airframe`,
          }),
          legalDocsTable(el, related, { apiUrl })
        );
      }

      fillMsnOptions("");
      fillMpdOptions();
      paintLegal();

      const wsDrop = fileDropzone(el, {
        label: "Drop workscope Excel/PDF here, or click to select",
        accept: ".pdf,.xls,.xlsx,.xlsm",
        inputRef: wsFileRef,
      });
      const supportDrop = fileDropzone(el, {
        label: "Drop support files here (optional)",
        accept: ".pdf,.xls,.xlsx,.xlsm,.eml",
        multiple: true,
        inputRef: supportFileRef,
      });
      const ldndDrop = fileDropzone(el, {
        label: "Drop LDND here if available (optional — inferred from upload)",
        accept: ".pdf,.xls,.xlsx,.xlsm",
        inputRef: ldndFileRef,
      });

      const legalFileInput = el("input", {
        type: "file",
        accept: ".pdf,.doc,.docx,.xls,.xlsx",
        class: "hidden",
      });
      legalFileRef.current = legalFileInput;
      legalFileInput.addEventListener("change", async () => {
        const file = legalFileInput.files?.[0];
        if (!file) return;
        try {
          const fd = new FormData();
          fd.append("file", file);
          // Optional override only — server classify is preferred when empty/"auto"
          if (legalTypeSelect.value && legalTypeSelect.value !== "AUTO") {
            fd.append("docType", legalTypeSelect.value);
          }
          fd.append("scopeLevel", "OPERATOR");
          if (selectedAsset?.operatorId) fd.append("operatorId", String(selectedAsset.operatorId));
          if (selectedAsset?.id) fd.append("assetId", String(selectedAsset.id));
          const res = await fetch(apiUrl("/api/ai/documents/upload"), {
            method: "POST",
            body: fd,
            credentials: "include",
          });
          const json = await res.json();
          if (!json.ok) throw new Error(json.error || "Upload failed");
          const classified = json.document?.docType || json.docType || legalTypeSelect.value || "LEASE";
          legalDocs = [
            {
              id: json.documentId || json.document?.id,
              docType: classified,
              title: file.name,
              originalFilename: file.name,
              status: json.document?.status || "UPLOADED",
              ocrStatus: json.document?.ocrStatus || null,
              createdAt: new Date().toISOString(),
              operatorId: selectedAsset?.operatorId,
              assetId: selectedAsset?.id,
            },
            ...legalDocs,
          ];
          paintLegal();
        } catch (err) {
          errEl.textContent = cleanText(err?.message) || String(err);
        }
        legalFileInput.value = "";
      });

      host.replaceChildren(
        el("div", { class: "space-y-4 max-w-4xl" }, [
          workscopesChrome(el, {
            title: "New evaluation — A. Setup",
            crumbs: [{ href: "#/figaro/workscopes", label: "Workscopes" }],
            actions: mpdNavActions(el),
          }),
          el("div", { class: "ws-card p-4 space-y-3" }, [
            el("div", { class: "text-sm font-semibold", text: "Aircraft & check" }),
            field(el, "Filter MSN", msnFilter),
            field(el, "MSN / airframe (brings operator)", msnSelect),
            fleetHint,
            field(el, "Title (check seeking approval)", titleIn),
            field(el, "Checks claiming / costing with lease", checkIn),
            field(el, "MPD revision", mpdSelect),
            field(el, "MRO / facility location", mroLocationIn),
            el("p", {
              class: "text-xs text-gray-500 -mt-2",
              text: "Where the check will be performed — used later to test lease approved-provider / geography requirements.",
            }),
            el("div", { class: "grid sm:grid-cols-2 gap-3" }, [
              field(el, "Check induction date", inductionIn),
              field(el, "Expected CRS date", crsIn),
            ]),
          ]),
          el("div", { class: "ws-card p-4 space-y-3" }, [
            el("div", { class: "text-sm font-semibold", text: "Workscope & support documents" }),
            el("p", {
              class: "text-xs text-gray-500",
              text: "Workscope is mandatory (PDF or Excel). Excel is preferred — all sheets are scanned for TASK_CARD / EC / MPD item columns.",
            }),
            field(el, "Workscope file (required)", wsDrop),
            field(el, "Additional support files", supportDrop),
            field(el, "LDND (optional)", ldndDrop),
            coverageHost,
          ]),
          el("div", { class: "ws-card p-4 space-y-2" }, [
            el("div", { class: "flex items-center justify-between gap-2" }, [
              el("div", { class: "text-sm font-semibold", text: "Legal documents (all MSN docs linked)" }),
              el("div", { class: "flex items-center gap-2" }, [
                legalTypeSelect,
                plusUploadBtn(el, {
                  title: "Upload legal document",
                  onClick: () => legalFileInput.click(),
                }),
              ]),
            ]),
            legalFileInput,
            legalHost,
            el("p", {
              class: "text-xs text-gray-500",
              text: "All matched MSN/operator legal docs are linked automatically — no checkboxes. Kind is system-identified; override upload type only if classification is wrong. Open icons open analysis / file in a new tab.",
            }),
          ]),
          field(el, "Notes", notesIn),
          errEl,
          el(
            "button",
            {
              type: "button",
              class: "rounded-lg bg-dae text-white text-sm font-medium px-4 py-2",
              onclick: async () => {
                errEl.textContent = "";
                coverageHost.textContent = "";
                if (!msnSelect.value) {
                  errEl.textContent = "Select an MSN.";
                  return;
                }
                if (!mpdSelect.value) {
                  errEl.textContent = "Confirm an MPD revision.";
                  return;
                }
                const wsFile = wsFileRef.current?.files?.[0];
                if (!wsFile) {
                  errEl.textContent = "Attach the workscope file (PDF or Excel).";
                  return;
                }
                const asset = assets.find((a) => String(a.id) === msnSelect.value);
                const relatedIds = (selectedAsset ? legalDocs.filter((d) => docMatchesAsset(d, selectedAsset)) : [])
                  .map((d) => Number(d.id || d.documentId))
                  .filter((n) => Number.isFinite(n) && n > 0);
                const fd = new FormData();
                fd.append("workscope", wsFile);
                for (const f of supportFileRef.current?.files || []) fd.append("support", f);
                if (ldndFileRef.current?.files?.[0]) fd.append("ldnd", ldndFileRef.current.files[0]);
                fd.append("title", titleIn.value.trim());
                fd.append("checkType", checkIn.value.trim());
                fd.append("mpdRevisionId", mpdSelect.value);
                fd.append("notes", notesIn.value.trim());
                if (mroLocationIn.value.trim()) fd.append("mroLocation", mroLocationIn.value.trim());
                if (inductionIn.value) fd.append("inductionDate", inductionIn.value);
                if (crsIn.value) fd.append("expectedCrsDate", crsIn.value);
                if (asset?.operatorId) fd.append("operatorId", String(asset.operatorId));
                if (asset?.aircraftId) fd.append("aircraftId", String(asset.aircraftId));
                if (asset?.id) fd.append("assetId", String(asset.id));
                if (asset?.serialNumber) fd.append("msn", asset.serialNumber);
                if (asset?.metadata?.registration) fd.append("registration", asset.metadata.registration);
                if (relatedIds.length) {
                  fd.append("legalDocumentIds", relatedIds.join(","));
                }
                try {
                  coverageHost.textContent = "Uploading & reading workscope…";
                  const res = await fetch(apiUrl("/api/ai/workscopes"), {
                    method: "POST",
                    body: fd,
                    credentials: "include",
                  });
                  const json = await res.json();
                  if (!json.ok) {
                    const tried = json.extract?.sheetsTried;
                    const hint = tried?.length
                      ? ` Sheets tried: ${tried.map((t) => `${t.sheet}(${t.tasks})`).join(", ")}`
                      : "";
                    throw new Error((json.error || "Create failed") + hint);
                  }
                  lastExtract = json.extract;
                  const cov = lastExtract?.coverage || {};
                  coverageHost.replaceChildren(
                    el("div", { class: "text-sm text-emerald-800 space-y-1" }, [
                      el("div", {
                        text: `Parsed ${lastExtract?.rowCount ?? 0} tasks · sheet ${lastExtract?.sheetName || "—"} · mandatory task # ${cov.taskNumber ? "OK" : "missing"} · titles ${cov.title ? "yes" : "no"} · hours ${cov.manHours ? "yes" : "no"}`,
                      }),
                      ...(lastExtract?.warnings || []).map((w) =>
                        el("div", { class: "text-amber-800 text-xs", text: w })
                      ),
                    ])
                  );
                  const newId = json.evaluation?.id;
                  if (newId) {
                    try {
                      await fetchJson(apiUrl(`/api/ai/workscopes/${newId}/stage`), {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ stage: "CLAUSES" }),
                      });
                    } catch {
                      /* stay SETUP */
                    }
                    location.hash = `#/figaro/workscopes/${newId}`;
                  } else {
                    location.hash = "#/figaro/workscopes";
                  }
                } catch (err) {
                  errEl.textContent = cleanText(err?.message) || String(err);
                }
              },
            },
            ["Create evaluation → Legal"]
          ),
        ])
      );
    } catch (e) {
      host.replaceChildren(
        el("div", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) })
      );
    }
  })();
}

function field(el, label, control) {
  return el("label", { class: "block space-y-1" }, [
    el("div", { class: "text-xs font-semibold uppercase tracking-wide text-gray-500", text: label }),
    control,
  ]);
}

function stageChip(el, letter, title, hint, active, onClick, done = false) {
  return el(
    "button",
    {
      type: "button",
      class: `ws-stage-btn${active ? " is-on" : ""}${done && !active ? " is-done" : ""}`,
      onclick: onClick || undefined,
    },
    [
      el("div", { class: "ws-stage-letter", text: letter }),
      el("div", { class: "ws-stage-title", text: title }),
      el("div", { class: "ws-stage-hint", text: hint }),
    ]
  );
}

function stepCardClass(active) {
  return `ws-card p-4 space-y-3${active ? " is-focus" : " is-dim"}`;
}

function answerToggle(el, current, onPick) {
  const mk = (val, activeClass, idleClass) =>
    el("button", {
      type: "button",
      class: current === val ? activeClass : idleClass,
      text: val,
      onclick: () => onPick(val),
    });
  return el("div", { class: "flex gap-1 shrink-0" }, [
    mk(
      "TBD",
      "rounded border border-amber-500 bg-amber-500 text-white text-xs px-2 py-1",
      "rounded border border-gray-200 bg-white text-gray-700 text-xs px-2 py-1"
    ),
    mk(
      "YES",
      "rounded border border-emerald-600 bg-emerald-600 text-white text-xs px-2 py-1",
      "rounded border border-gray-200 bg-white text-gray-700 text-xs px-2 py-1"
    ),
    mk(
      "NO",
      "rounded border border-red-600 bg-red-600 text-white text-xs px-2 py-1",
      "rounded border border-gray-200 bg-white text-gray-700 text-xs px-2 py-1"
    ),
  ]);
}

function renderEvaluationDetail({ el, fetchJson, apiUrl, cleanText, host, id }) {
  host.replaceChildren(el("div", { class: "text-sm text-gray-500", text: "Loading evaluation…" }));

  async function paint() {
    const data = await fetchJson(apiUrl(`/api/ai/workscopes/${id}`));
    const ev = data.evaluation;
    if (!ev) throw new Error("Not found");

    const result = ev.result || null;
    const pending = ev.pendingQuestions || [];
    const answersDraft = { ...(ev.answeredQuestions || {}) };
    for (const q of pending) {
      if (answersDraft[q.id] == null) answersDraft[q.id] = q.answer || "TBD";
    }
    let stage = normalizeStage(ev.stage || "SETUP");
    if (stage === "DONE") stage = "REPORT";
    const stageIdx = wizardIndex(stage);
    const extract = ev.workscopeExtract || null;
    const validation = ev.validationReport || null;
    const confirmation = ev.confirmationStatus || {};
    const msgEl = el("div", { class: "text-sm text-gray-600" });
    const qsHost = el("div", { class: "space-y-2" });
    const confirmEl = el("div", {
      class: `text-xs font-semibold rounded-lg px-2.5 py-1.5 inline-flex ${
        confirmation.complete === false ? "ws-confirm-needs" : "ws-confirm-ok"
      }`,
      text: confirmation.label || "Effectivity status",
    });

    async function goStage(next) {
      const target = normalizeStage(next);
      // Soft-build draft when entering D/E/F so panels are never empty after Next without Confirm.
      if (["WORKSCOPE", "MPD_GAPS", "REPORT"].includes(target)) {
        try {
          msgEl.textContent = "Building operator workscope draft…";
          await fetchJson(apiUrl(`/api/ai/workscopes/${id}/ensure-draft`), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ softConfirm: true }),
          });
        } catch (err) {
          msgEl.textContent = cleanText(err?.message) || String(err);
          msgEl.className = "text-sm text-red-700";
        }
      }
      await fetchJson(apiUrl(`/api/ai/workscopes/${id}/stage`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage: target }),
      });
      await paint();
    }

    async function runClauseAnalysis({ force = false } = {}) {
      const current = ev.leaseEligibility || null;
      if (!force && current?.intelligence?.builtAt && (current?.conditions || []).length) {
        return;
      }
      msgEl.textContent = "Interpreting lease for claimed check…";
      msgEl.className = "text-sm text-gray-600";
      await fetchJson(apiUrl(`/api/ai/workscopes/${id}/lease-eligibility/build`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      // Soft-confirm so Next can proceed — show we know what we're analyzing
      try {
        const fresh = await fetchJson(apiUrl(`/api/ai/workscopes/${id}`));
        const conds = (fresh.evaluation?.leaseEligibility?.conditions || []).map((c) => ({
          id: c.id,
          code: c.code,
          eligible: c.eligible !== false,
          citation: c.citation,
        }));
        if (conds.length) {
          await fetchJson(apiUrl(`/api/ai/workscopes/${id}/lease-eligibility/confirm`), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ conditions: conds, softConfirm: true, simulate: false }),
          });
        }
      } catch {
        /* soft-confirm optional */
      }
      msgEl.textContent = "Lease interpretation ready — review clauses, then Next.";
    }

    function paintQuestions() {
      qsHost.replaceChildren();
      if (!pending.length) {
        qsHost.appendChild(
          el("div", {
            class: "text-sm text-gray-500",
            text:
              "No applicability conditions yet. Run “Build questions” after workscope analysis — ALL effectivity is never asked. OR model lines are atomic and deduped.",
          })
        );
        return;
      }
      for (const q of pending) {
        const condition = q.condition || q.prompt || q.token || "";
        const row = el("div", {
          class: "flex flex-wrap items-start gap-2 py-2 border-b border-gray-50",
        });
        const textCol = el("div", { class: "flex-1 min-w-[12rem] space-y-0.5" }, [
          el("div", { class: "text-sm text-gray-900", text: condition }),
          q.noteRef
            ? el("div", { class: "text-[11px] text-amber-800", text: q.noteRef })
            : null,
          q.groupLogic || q.logic
            ? el("div", {
                class: "text-[10px] uppercase tracking-wide text-gray-400",
                text: q.groupLogic === "OR" || q.logic === "OR_ATOM" ? "OR atom" : q.logic || q.groupLogic,
              })
            : null,
        ]);
        const toggles = answerToggle(el, answersDraft[q.id] || "TBD", async (val) => {
          answersDraft[q.id] = val;
          paintQuestions();
          try {
            msgEl.textContent = "Saving…";
            const out = await fetchJson(apiUrl(`/api/ai/workscopes/${id}/questions/answer-one`), {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ questionId: q.id, answer: val }),
            });
            const cs = out.confirmationStatus || out.evaluation?.confirmationStatus;
            if (cs?.label) {
              confirmEl.textContent = cs.label;
              confirmEl.className = `text-xs font-semibold rounded-lg px-2.5 py-1.5 inline-flex ${
                cs.complete === false ? "ws-confirm-needs" : "ws-confirm-ok"
              }`;
            }
            msgEl.textContent = "Saved.";
          } catch (err) {
            msgEl.textContent = cleanText(err?.message) || String(err);
            msgEl.className = "text-sm text-red-700";
          }
        });
        row.appendChild(textCol);
        row.appendChild(toggles);
        qsHost.appendChild(row);
      }
    }
    paintQuestions();

    const coverage = extract?.coverage || validation?.coverage || {};
    const covLines = [
      el("div", {
        class: "text-sm",
        text: extract
          ? `Workscope extract: ${extract.rowCount ?? extract.tasks?.length ?? 0} task rows${
              extract.sheetName ? ` · sheet “${extract.sheetName}”` : ""
            }`
          : "Workscope file linked — open Build questions to analyze effectivity.",
      }),
      el("div", {
        class: "text-xs text-gray-600",
        text: `Mandatory task #: ${coverage.taskNumber ? "present" : "missing / unknown"} · titles ${
          coverage.title ? "yes" : "nice-to-have"
        } · man-hours ${coverage.manHours ? "yes" : "nice-to-have"} · intervals ${
          coverage.intervalRaw ? "yes" : "nice-to-have"
        }`,
      }),
      ...(extract?.warnings || []).map((w) =>
        el("div", { class: "text-xs text-amber-800", text: w })
      ),
    ];

    const tableA = result?.tableA || [];
    const notEligible = tableA.filter(
      (r) =>
        r.leaseEligible === false ||
        r.flags?.includes?.("NOT_IN_MPD") ||
        r.flags?.includes?.("EFFECTIVITY_NO")
    );
    const shouldInclude = (result?.tableB || []).filter((r) => r.status === "MISSING_FROM_WORKSCOPE");
    const eligibleCount = tableA.filter((r) => r.leaseEligible === true && r.mpdMatch).length;
    const leaseElig = ev.leaseEligibility || null;
    let draftFilter = "all";

    const legalDocs = (ev.documents || []).filter(
      (d) =>
        ["LEASE", "LEASE_AMENDMENT", "OTHER_LEGAL"].includes(d.docType) ||
        ["LEASE", "LEASE_AMENDMENT", "OTHER_LEGAL"].includes(d.role)
    );
    const otherDocs = (ev.documents || []).filter((d) => !legalDocs.includes(d));

    const leaseHost = el("div", { class: "space-y-3" });

    function paintLease() {
      const conditions = leaseElig?.conditions || [];
      const intel = leaseElig?.intelligence || null;
      const gov = leaseElig?.governingDocument;
      const govKind = gov?.docType || gov?.role || "LEASE";
      const govDate = fmtDateShort(gov?.date || gov?.documentDate);
      const extracts = extractsFromLeaseConditions(conditions, gov);
      // Also fold intelligence citations into extracts for open-in-new-tab
      const intelExtracts = [];
      if (intel) {
        for (const d of intel.definitions || []) {
          if (d.citation?.documentId && d.text) {
            intelExtracts.push({
              id: `def-${d.term || d.text.slice(0, 12)}`,
              label: d.term ? `Definition: ${d.term}` : "Definition",
              text: d.text,
              documentId: d.citation.documentId,
              documentKind: d.citation.docType || "LEASE",
              documentName: d.citation.title,
              clauseRef: d.citation.clause,
              quote: d.citation.quote || d.text,
            });
          }
        }
        for (const block of [
          intel.approvedMaintenanceProvider,
          intel.claimedCheckMeaning,
          intel.lesserChecks,
          intel.eligibilityBasis,
        ]) {
          for (const c of block?.citations || []) {
            if (!c?.documentId) continue;
            intelExtracts.push({
              id: `intel-${c.documentId}-${c.clause || c.page || Math.random()}`,
              label: block === intel.approvedMaintenanceProvider
                ? "Approved MRO / location"
                : block === intel.claimedCheckMeaning
                  ? `Claimed check (${ev.checkType || "?"})`
                  : block === intel.lesserChecks
                    ? "Lesser checks"
                    : "Eligibility basis",
              text: c.quote || c.excerpt || block.summary || "",
              documentId: c.documentId,
              documentKind: c.docType || "LEASE",
              documentName: c.title,
              clauseRef: c.clause,
              quote: c.quote || c.excerpt,
            });
          }
        }
      }

      const clauseRows = conditions
        .map((c) => {
          const cite = c.citation || gov || {};
          const literal = String(
            cite.quote ||
              cite.excerpt ||
              cite.note ||
              c.label ||
              `Confirm ${c.code || "package"} wording in the linked lease / amendment.`
          )
            .replace(/\s+/g, " ")
            .trim();
          const clauseRef = cite.clause || c.conditionRef || null;
          const safeRef =
            clauseRef && String(clauseRef).toLowerCase() !== "null" ? String(clauseRef) : "—";
          return {
            code: c.code,
            id: c.id || c.code,
            kind: cite.docType || cite.role || govKind || "LEASE",
            clauseRef: safeRef,
            literal: literal && literal.toLowerCase() !== "null" ? literal.slice(0, 280) : "—",
            eligible: c.eligible !== false,
            citation: cite,
            openId: cite.documentId || gov?.documentId,
            source: c.source || null,
          };
        })
        .filter((r) => r.code || r.literal !== "—");

      const packagesFound = intel?.packagesFoundInLease;
      const zeroTime = intel?.zeroTimeRequirement;

      leaseHost.replaceChildren(
        el("div", { class: "ws-callout space-y-1.5" }, [
          el("div", {
            class: "text-sm font-semibold text-gray-900",
            text: "B — Clauses (legal + MR packages)",
          }),
          el("p", {
            class: "text-xs text-gray-700",
            text:
              "Lease interpretation runs automatically for the claimed check. Snapshot is document kind · clause ref · short extract — not a wall of comments. Packages come from the lease, not from workscope intervals.",
          }),
          packagesFound === false
            ? el("p", {
                class: "text-xs text-amber-900 font-semibold",
                text: `No MR packages found in lease text for ${ev.checkType || "claimed check"} — showing setup claimed-check only (not invented from workscope).`,
              })
            : null,
          zeroTime?.required === true
            ? el("p", {
                class: "text-xs text-amber-900 font-semibold",
                text: `Zero-time / bring-back-to-zero: ${String(zeroTime.summary || "Lease requires check brought back to zero time.").slice(0, 280)}`,
              })
            : null,
        ]),
        // Compact legal index strip (not the main UI)
        el("div", { class: "rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 space-y-1.5" }, [
          el("div", {
            class: "text-[10px] uppercase tracking-wide text-gray-400 font-semibold",
            text: "Legal library on this evaluation (index / OCR)",
          }),
          legalDocs.length
            ? el(
                "div",
                { class: "flex flex-wrap gap-1.5" },
                legalDocs.map((d) =>
                  el("a", {
                    href: docAnalysisHref(d.documentId),
                    target: "_blank",
                    rel: "noopener",
                    class: "inline-flex items-center gap-1.5 text-[11px] rounded-md border border-gray-200 bg-white px-2 py-1 hover:border-dae text-gray-800",
                    title: d.title || d.originalFilename,
                  }, [
                    el("span", {
                      class: "uppercase font-semibold text-gray-500",
                      text: d.docType || d.role || "LEGAL",
                    }),
                    el("span", {
                      class: String(d.status || "").toUpperCase() === "INDEXED"
                        ? "text-emerald-700 font-semibold"
                        : "text-amber-800 font-semibold",
                      text: d.status || "—",
                    }),
                    d.ocrStatus
                      ? el("span", { class: "text-gray-400", text: d.ocrStatus })
                      : null,
                  ])
                )
              )
            : el("div", {
                class: "text-xs text-amber-800",
                text: "No lease linked yet — upload with +.",
              }),
          gov
            ? el("div", { class: "text-xs text-gray-600" }, [
                el("span", { text: "Governing: " }),
                el("span", { class: "font-semibold", text: `${govKind}` }),
                el("span", { text: ` · executed ${govDate}` }),
                leaseElig.status === "CONFIRMED"
                  ? el("span", { class: "text-emerald-700 font-semibold", text: " · soft-confirmed" })
                  : el("span", { class: "text-amber-700", text: " · interpreting…" }),
              ])
            : null,
        ]),
        // Lease intelligence block — compact
        intel
          ? el("div", { class: "rounded-lg border border-amber-100 bg-amber-50/60 px-3 py-2.5 space-y-2" }, [
              el("div", {
                class: "text-xs font-semibold text-amber-950",
                text: `Lease intelligence · ${intel.tier || "—"}${intel.fullSweep ? " (deep)" : ""}`,
              }),
              el("div", { class: "grid sm:grid-cols-2 gap-2 text-xs text-gray-800" }, [
                el("div", {}, [
                  el("div", { class: "font-semibold text-gray-500 uppercase text-[10px]", text: "Definitions" }),
                  el("div", {
                    text: (intel.definitions || []).length
                      ? intel.definitions
                          .slice(0, 3)
                          .map((d) => (d.term ? `${d.term}: ${d.text}` : d.text))
                          .filter(Boolean)
                          .join(" · ")
                          .slice(0, 280) || "—"
                      : "—",
                  }),
                ]),
                el("div", {}, [
                  el("div", { class: "font-semibold text-gray-500 uppercase text-[10px]", text: "Approved MRO / location" }),
                  el("div", {
                    text: [
                      intel.approvedMaintenanceProvider?.summary,
                      intel.approvedMaintenanceProvider?.locationRequirements,
                      ev.mroLocation
                        ? `Proposed MRO: ${ev.mroLocation}${
                            intel.approvedMaintenanceProvider?.meetsProposedLocation
                              ? ` (${intel.approvedMaintenanceProvider.meetsProposedLocation})`
                              : ""
                          }`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" — ")
                      .slice(0, 320) || "—",
                  }),
                ]),
                el("div", {}, [
                  el("div", { class: "font-semibold text-gray-500 uppercase text-[10px]", text: `What ${ev.checkType || "claimed check"} means` }),
                  el("div", {
                    text: (intel.claimedCheckMeaning?.summary || "—").slice(0, 280),
                  }),
                ]),
                el("div", {}, [
                  el("div", { class: "font-semibold text-gray-500 uppercase text-[10px]", text: "Lesser checks in MR?" }),
                  el("div", {
                    text: [
                      intel.lesserChecks?.allowedOrIncludedInMr == null
                        ? "Unknown"
                        : intel.lesserChecks.allowedOrIncludedInMr
                          ? "Yes / included"
                          : "No / not included",
                      intel.lesserChecks?.summary,
                    ]
                      .filter(Boolean)
                      .join(" — ")
                      .slice(0, 280),
                  }),
                ]),
                el("div", {}, [
                  el("div", { class: "font-semibold text-gray-500 uppercase text-[10px]", text: "Eligibility basis" }),
                  el("div", {
                    text: [
                      intel.eligibilityBasis?.mode || "UNKNOWN",
                      intel.eligibilityBasis?.summary,
                    ]
                      .filter(Boolean)
                      .join(" — ")
                      .slice(0, 320),
                  }),
                ]),
                el("div", {}, [
                  el("div", { class: "font-semibold text-gray-500 uppercase text-[10px]", text: "Zero-time / bring-back-to-zero" }),
                  el("div", {
                    text: [
                      zeroTime?.required === true
                        ? "Required"
                        : zeroTime?.required === false
                          ? "Not required"
                          : "Unknown",
                      zeroTime?.summary,
                    ]
                      .filter(Boolean)
                      .join(" — ")
                      .slice(0, 280) || "—",
                  }),
                ]),
              ]),
              (intel.notes || []).length
                ? el("div", {
                    class: "text-[11px] text-amber-900/90",
                    text: intel.notes.slice(0, 3).join(" · "),
                  })
                : null,
            ])
          : el("div", {
              class: "text-xs text-gray-500",
              text: "Running lease interpretation…",
            }),
        // Clause snapshot table — protagonists
        clauseRows.length
          ? el("div", { class: "overflow-x-auto" }, [
              el("table", { class: "ws-table" }, [
                el("thead", {}, [
                  el("tr", {}, [
                    el("th", { text: "Document kind" }),
                    el("th", { text: "Clause ref" }),
                    el("th", { text: "Literal extract" }),
                    el("th", { text: "MR package" }),
                    el("th", { text: "In MR?" }),
                    el("th", { text: "Open" }),
                  ]),
                ]),
                el(
                  "tbody",
                  {},
                  clauseRows.map((row) => {
                    const cb = el("input", {
                      type: "checkbox",
                      checked: row.eligible,
                      class: "rounded border-gray-300",
                      title: "Reference alignment — tick if this package is MR-reimbursable",
                    });
                    cb.addEventListener("change", () => {
                      const c = conditions.find((x) => String(x.id || x.code) === String(row.id));
                      if (c) c.eligible = cb.checked;
                      row.eligible = cb.checked;
                    });
                    return el("tr", {}, [
                      el("td", {
                        class: "text-[10px] uppercase font-semibold text-gray-500 whitespace-nowrap",
                        text: row.kind,
                      }),
                      el("td", { class: "font-mono text-xs", text: row.clauseRef }),
                      el("td", {
                        class: "text-xs max-w-md",
                        text: row.literal,
                      }),
                      el("td", { class: "font-mono text-xs font-semibold", text: row.code || "—" }),
                      el("td", {}, [cb]),
                      el("td", {}, [
                        row.openId
                          ? el("a", {
                              href: docAnalysisHref(row.openId, {
                                q: String(row.literal || row.code || "").slice(0, 80),
                              }),
                              target: "_blank",
                              rel: "noopener",
                              class: "text-xs text-dae hover:underline font-semibold",
                              text: "Analysis ↗",
                            })
                          : el("span", { class: "text-xs text-gray-400", text: "—" }),
                      ]),
                    ]);
                  })
                ),
              ]),
            ])
          : el("div", {
              class: "text-xs text-gray-500",
              text: "No MR clause packages yet — interpretation will populate this table.",
            }),
        extracts.length || intelExtracts.length
          ? el("div", { class: "space-y-1" }, [
              el("p", {
                class: "text-xs text-gray-500",
                text: "Source extracts — open analysis for full document context.",
              }),
              renderDocExtractsViewer({
                el,
                fetchJson,
                apiUrl,
                cleanText,
                extracts: [...extracts, ...intelExtracts],
                documents: leaseElig?.documentsConsidered || legalDocs,
                contextTitle: "Clause extracts",
                compact: true,
                showOriginal: false,
              }),
            ])
          : null
      );
    }
    paintLease();

    const draftHost = el("div", { class: "space-y-3" });
    function filteredDraftRows() {
      if (draftFilter === "eligible") return tableA.filter((r) => r.leaseEligible === true);
      if (draftFilter === "not_eligible") return notEligible;
      if (draftFilter === "missing_mpd") return tableA.filter((r) => !r.mpdMatch);
      if (draftFilter === "tbd") {
        return tableA.filter(
          (r) => r.effectivityStatus === "TBD" || r.flags?.includes?.("EFFECTIVITY_TBD")
        );
      }
      return tableA;
    }
    function paintDraft() {
      const rows = filteredDraftRows();
      const filters = [
        ["all", "All"],
        ["eligible", "Eligible"],
        ["not_eligible", "Not eligible"],
        ["missing_mpd", "Not in MPD"],
        ["tbd", "TBD effectivity"],
      ];
      const leasePending = leaseElig && leaseElig.status !== "CONFIRMED";
      draftHost.replaceChildren(
        result && tableA.length
          ? el("div", { class: "space-y-3" }, [
              el("div", { class: "grid sm:grid-cols-2 gap-3 text-sm" }, [
                summaryCell(el, "MR eligible", eligibleCount),
                summaryCell(el, "Not eligible / gaps", notEligible.length),
              ]),
              el("div", {
                class: "text-xs text-gray-500",
                text: [
                  "Operator workscope lines with eligibility annotated from lease MR packages + MPD match",
                  result.summary?.mpdOem
                    ? `MPD ${result.summary.mpdOem} Rev ${result.summary.mpdRevision || "?"}`
                    : null,
                  result.summary?.leaseStatus ? `Lease ${result.summary.leaseStatus}` : null,
                  confirmation.complete === false ? confirmation.label : null,
                  `generated ${fmtDate(result.generatedAt)}`,
                ]
                  .filter(Boolean)
                  .join(" · "),
              }),
              el(
                "div",
                { class: "flex flex-wrap gap-1.5" },
                filters.map(([key, label]) =>
                  el("button", {
                    type: "button",
                    class: `ws-filter-chip${draftFilter === key ? " is-on" : ""}`,
                    text: label,
                    onclick: () => {
                      draftFilter = key;
                      paintDraft();
                    },
                  })
                )
              ),
              el("div", { class: "overflow-hidden border border-gray-200 rounded-xl" }, [
                el("div", {
                  class: "px-4 py-3 border-b border-gray-100 text-sm font-semibold bg-white",
                  text: `Operator workscope (${rows.length} of ${tableA.length} lines)`,
                }),
                el("div", { class: "overflow-x-auto max-h-[36rem]" }, [
                  el("table", { class: "ws-table" }, [
                    el("thead", {}, [
                      el("tr", {}, [
                        el("th", { text: "Task (normalized)" }),
                        el("th", { text: "MR eligible" }),
                        el("th", { text: "Why not / notes" }),
                        el("th", { text: "MR package" }),
                        el("th", { text: "In MPD" }),
                        el("th", { text: "MPD task #" }),
                        el("th", { text: "MPD interval" }),
                        el("th", { text: "LDND vs CRS" }),
                      ]),
                    ]),
                    el(
                      "tbody",
                      {},
                      rows.map((r) => {
                        const taskLabel = r.workscopeTaskRaw
                          ? `${r.workscopeTask}  (${r.workscopeTaskRaw})`
                          : r.workscopeTask;
                        const elig =
                          r.leaseEligible === true ? "Yes" : r.leaseEligible === false ? "No" : "—";
                        const ldnd =
                          r.ldndStatus ||
                          (ev.ldndProvided ? r.ldndNote || "See LDND" : "No LDND");
                        return el("tr", {}, [
                          el("td", {
                            class: "font-mono text-xs",
                            text: String(taskLabel || "—").slice(0, 80),
                          }),
                          el("td", { class: "text-xs font-semibold", text: elig }),
                          el("td", {
                            class: "text-xs",
                            text: (r.notEligibleReason || r.leaseCriteria || "—").slice(0, 180),
                          }),
                          el("td", {
                            class: "text-xs",
                            text: (r.leaseConditionRef || "—").slice(0, 60),
                          }),
                          el("td", { class: "text-xs", text: r.mpdMatch ? "Yes" : "No" }),
                          el("td", {
                            class: "font-mono text-xs",
                            text: r.mpdMatch ? r.mpdTask || "matched" : "—",
                          }),
                          el("td", {
                            class: "font-mono text-xs",
                            text: (r.mpdInterval || r.workscopeInterval || "—").slice(0, 40),
                          }),
                          el("td", { class: "text-xs", text: String(ldnd).slice(0, 40) }),
                        ]);
                      })
                    ),
                  ]),
                ]),
              ]),
            ])
          : el("div", {
              class: "text-sm text-gray-500",
              text: leasePending
                ? "Building draft… Advance from Clauses so lease packages soft-confirm, then return here."
                : result
                  ? `Draft has ${tableA.length} workscope line(s).`
                  : "No operator workscope draft yet — advancing to D builds it automatically (soft-confirm + simulate).",
            })
      );
    }
    paintDraft();

    const legalUploadInput = el("input", {
      type: "file",
      accept: ".pdf,.doc,.docx",
      class: "hidden",
    });
    const legalTypeSelect = el(
      "select",
      { class: "border border-gray-200 rounded-lg px-2 py-1 text-xs" },
      [
        el("option", { value: "LEASE", text: "Lease" }),
        el("option", { value: "LEASE_AMENDMENT", text: "Amendment" }),
        el("option", { value: "OTHER_LEGAL", text: "Other legal" }),
      ]
    );
    legalUploadInput.addEventListener("change", async () => {
      const file = legalUploadInput.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("role", legalTypeSelect.value);
      fd.append("docType", legalTypeSelect.value);
      msgEl.textContent = "Uploading legal doc…";
      try {
        const res = await fetch(apiUrl(`/api/ai/workscopes/${id}/upload`), {
          method: "POST",
          body: fd,
          credentials: "include",
        });
        const json = await res.json();
        if (!json.ok) throw new Error(json.error || "Upload failed");
        await paint();
      } catch (err) {
        msgEl.textContent = cleanText(err?.message) || String(err);
        msgEl.className = "text-sm text-red-700";
      }
      legalUploadInput.value = "";
    });

    // Auto-build questions once if empty
    if (!pending.length && ev.mpdRevisionId && !questionsAutoTried.has(Number(id))) {
      questionsAutoTried.add(Number(id));
      fetchJson(apiUrl(`/api/ai/workscopes/${id}/questions/build`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      })
        .then(() => paint())
        .catch(() => {});
    }

    // Auto-run lease interpretation once on clauses stage
    if (
      (stage === "CLAUSES" || stage === "LEGAL") &&
      !clausesAutoTried.has(Number(id)) &&
      !(leaseElig?.intelligence?.builtAt && (leaseElig?.conditions || []).length)
    ) {
      clausesAutoTried.add(Number(id));
      runClauseAnalysis()
        .then(() => paint())
        .catch((err) => {
          msgEl.textContent = cleanText(err?.message) || String(err);
          msgEl.className = "text-sm text-red-700";
        });
    }

    const notDueYet = (result?.tableB || []).filter((r) => r.status === "NOT_DUE_YET");
    const effectivityInventory =
      ev.validationReport?.effectivityInventory || result?.summary?.effectivityInventory || null;
    const zeroTimeSummary =
      result?.summary?.zeroTimeRequirement || leaseElig?.intelligence?.zeroTimeRequirement;

    host.replaceChildren(
      el("div", { class: "space-y-5" }, [
        workscopesChrome(el, {
          title: ev.title || `Evaluation #${ev.id}`,
          crumbs: [{ href: "#/figaro/workscopes", label: "Workscopes" }],
          actions: el("div", { class: "flex flex-wrap gap-2 items-center" }, [
            mpdNavActions(el),
            el("button", {
              type: "button",
              class: "ws-icon-btn ws-icon-btn-danger",
              title: "Delete evaluation",
              text: "×",
              onclick: async () => {
                if (
                  !confirm(
                    "Delete this evaluation? Linked documents stay in the library (unlink only — legal docs are never deleted)."
                  )
                )
                  return;
                await fetchJson(apiUrl(`/api/ai/workscopes/${id}`), { method: "DELETE" });
                location.hash = "#/figaro/workscopes";
              },
            }),
          ]),
        }),
        el("div", { class: "ws-stage-rail-wrap" }, [
          el(
            "div",
            { class: "ws-stage-rail", id: "ws-stage-rail" },
            WIZARD_STAGES.map((s, i) => {
              const active =
                stage === s.id ||
                (stage === "DONE" && s.id === "REPORT") ||
                (stage === "LEGAL" && s.id === "CLAUSES");
              return stageChip(el, s.letter, s.title, s.hint, active, () => goStage(s.id), i < stageIdx);
            })
          ),
        ]),
        el("div", { class: "flex flex-wrap items-center gap-3" }, [
          statusPill(el, ev.status),
          confirmEl,
          leaseElig?.status === "CONFIRMED"
            ? el("span", {
                class: "text-xs font-semibold rounded-lg px-2.5 py-1.5 inline-flex ws-confirm-ok",
                text: "Lease MR soft-confirmed",
              })
            : el("span", {
                class: "text-xs font-semibold rounded-lg px-2.5 py-1.5 inline-flex ws-confirm-needs",
                text: "Lease MR pending",
              }),
          ev.mpdRevisionId
            ? el("a", { href: `#/figaro/workscopes/mpds/${ev.mpdRevisionId}`, class: "inline-flex" }, [
                revBadge(el, ev.mpdRevisionNumber),
              ])
            : revBadge(el, ev.mpdRevisionNumber),
          el("span", {
            class: "text-sm text-gray-600",
            text: [ev.operatorName, ev.msn && `MSN ${ev.msn}`, ev.checkType].filter(Boolean).join(" · "),
          }),
        ]),

        el("div", { class: `ws-card p-4 space-y-3 ws-wizard-panel${stage === "SETUP" ? " is-on" : ""}` }, [
          el("div", { class: "text-sm font-semibold", text: "A — Setup" }),
          el("p", {
            class: "text-xs text-gray-500",
            text: "Aircraft, claimed checks, MRO location, workscope/support/LDND uploads, induction + expected CRS dates.",
          }),
          el("div", {
            class: "text-sm text-gray-800",
            text: [
              ev.title,
              ev.msn && `MSN ${ev.msn}`,
              ev.mpdRevisionNumber && `MPD Rev ${ev.mpdRevisionNumber}`,
              ev.checkType && `Claiming ${ev.checkType}`,
            ]
              .filter(Boolean)
              .join(" · "),
          }),
          el("div", { class: "grid sm:grid-cols-3 gap-3 text-sm" }, [
            el("div", {}, [
              el("div", { class: "text-xs text-gray-500", text: "MRO / facility location" }),
              el("div", { text: ev.mroLocation || "—" }),
            ]),
            el("div", {}, [
              el("div", { class: "text-xs text-gray-500", text: "Check induction date" }),
              el("div", { text: fmtDateShort(ev.inductionDate) }),
            ]),
            el("div", {}, [
              el("div", { class: "text-xs text-gray-500", text: "Expected CRS date" }),
              el("div", { text: fmtDateShort(ev.expectedCrsDate) }),
            ]),
          ]),
          el("div", { class: "space-y-1" }, covLines),
          otherDocs.length
            ? el("div", { class: "pt-1 space-y-1" }, [
                el("div", { class: "text-xs font-semibold text-gray-500", text: "Workscope / support / LDND" }),
                ...otherDocs.map((d) =>
                  el("div", { class: "flex flex-wrap items-baseline gap-2 text-sm" }, [
                    el("span", {
                      class: "text-[10px] uppercase text-gray-400 font-semibold",
                      text: d.role || d.docType,
                    }),
                    el("a", {
                      href: docAnalysisHref(d.documentId),
                      target: "_blank",
                      rel: "noopener",
                      class: "text-dae hover:underline",
                      text: d.title || d.originalFilename,
                    }),
                    el("span", { class: "text-xs text-gray-400", text: d.status || "" }),
                  ])
                ),
              ])
            : el("div", {
                class: "text-xs text-gray-500",
                text: "No workscope/support files linked yet — re-create or upload from a new evaluation if needed.",
              }),
          wizardNav(el, {
            stage,
            onBack: null,
            onNext: async () => {
              await goStage("CLAUSES");
            },
            nextLabel: "Next: Clauses →",
          }),
        ]),

        el("div", {
          class: `ws-card p-4 space-y-3 ws-wizard-panel${
            stage === "CLAUSES" || stage === "LEGAL" ? " is-on" : ""
          }`,
        }, [
          el("div", { class: "flex flex-wrap items-center justify-between gap-2" }, [
            el("div", { class: "text-sm font-semibold", text: "B — Clauses" }),
            el("div", { class: "flex items-center gap-2" }, [
              legalTypeSelect,
              plusUploadBtn(el, {
                title: "Upload legal / lease",
                onClick: () => legalUploadInput.click(),
              }),
              legalUploadInput,
            ]),
          ]),
          leaseHost,
          msgEl,
          wizardNav(el, {
            stage,
            onBack: () => goStage("SETUP"),
            onNext: async () => {
              try {
                const conditions = (leaseElig?.conditions || []).map((c) => ({
                  id: c.id,
                  code: c.code,
                  eligible: c.eligible !== false,
                  citation: c.citation,
                }));
                if (conditions.length && leaseElig?.status !== "CONFIRMED") {
                  await fetchJson(apiUrl(`/api/ai/workscopes/${id}/lease-eligibility/confirm`), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ conditions, softConfirm: true, simulate: false }),
                  });
                }
              } catch {
                /* still advance */
              }
              await goStage("EFFECTIVITY");
            },
            nextLabel: "Next: Effectivity →",
          }),
        ]),

        el("div", { class: `ws-card p-4 space-y-3 ws-wizard-panel${stage === "EFFECTIVITY" ? " is-on" : ""}` }, [
          el("div", { class: "text-sm font-semibold", text: "C — Effectivity / applicability" }),
          el("p", {
            class: "text-xs text-gray-500",
            text: "Against the MPD selected in setup: all non-ALL applicability conditions (models, ENG/config, APL atoms, notes). ALL is never asked. Answers auto-save.",
          }),
          effectivityInventory
            ? el("div", { class: "rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 space-y-1" }, [
                el("div", {
                  class: "font-semibold text-gray-800",
                  text: `MPD effectivity inventory · ${effectivityInventory.oem || "OEM?"} · ${effectivityInventory.questionCount ?? 0} condition(s)`,
                }),
                el("div", {
                  text: [
                    `${effectivityInventory.nonAllTaskCount ?? 0} non-ALL task rows`,
                    `${effectivityInventory.allOnlyTaskCount ?? 0} ALL-only`,
                    effectivityInventory.kindCounts
                      ? Object.entries(effectivityInventory.kindCounts)
                          .map(([k, v]) => `${k}:${v}`)
                          .join(" · ")
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · "),
                }),
                effectivityInventory.recommendation
                  ? el("div", {
                      class: effectivityInventory.onlyModelConditions
                        ? "text-amber-900 font-semibold"
                        : "text-gray-600",
                      text: effectivityInventory.recommendation,
                    })
                  : null,
              ])
            : null,
          qsHost,
          el("div", { class: "flex flex-wrap gap-2 pt-1" }, [
            actionBtn(el, "Rebuild applicability from MPD", async () => {
              msgEl.textContent = "Building questions from MPD effectivity…";
              await fetchJson(apiUrl(`/api/ai/workscopes/${id}/questions/build`), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: "{}",
              });
              await paint();
            }),
          ]),
          wizardNav(el, {
            stage,
            onBack: () => goStage("CLAUSES"),
            onNext: () => goStage("WORKSCOPE"),
            nextLabel: "Next: Workscope →",
          }),
        ]),

        el("div", { class: `ws-card p-4 space-y-3 ws-wizard-panel${stage === "WORKSCOPE" ? " is-on" : ""}` }, [
          el("div", { class: "text-sm font-semibold", text: "D — Operator workscope review" }),
          el("p", {
            class: "text-xs text-gray-500",
            text: "Operator workscope lines with MPD match, interval, LDND vs CRS, and MR eligibility. Lease clause UI stays on B.",
          }),
          draftHost,
          wizardNav(el, {
            stage,
            onBack: () => goStage("EFFECTIVITY"),
            onNext: () => goStage("MPD_GAPS"),
            nextLabel: "Next: MPD gaps →",
          }),
        ]),

        el("div", { class: `ws-card p-4 space-y-3 ws-wizard-panel${stage === "MPD_GAPS" ? " is-on" : ""}` }, [
          el("div", { class: "text-sm font-semibold", text: "E — MPD gaps (eligible but missing)" }),
          el("p", {
            class: "text-xs text-gray-500",
            text: "MPD tasks eligible for this visit / lease but missing from the operator workscope. LDND filters tasks not yet due by CRS/induction.",
          }),
          notDueYet.length
            ? el("div", {
                class: "text-xs text-amber-900 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2",
                text: `${notDueYet.length} task(s) annotated “not due yet” via LDND (excluded from hard gaps).`,
              })
            : ev.ldndProvided
              ? el("div", {
                  class: "text-xs text-gray-500",
                  text: ev.ldndExtract?.ok
                    ? `LDND parsed (${ev.ldndExtract.rowCount || ev.ldndExtract.tasks?.length || 0} rows).`
                    : "LDND linked but could not parse task/next-due columns.",
                })
              : el("div", {
                  class: "text-xs text-gray-500",
                  text: "No LDND provided — gaps are not filtered by due date.",
                }),
          shouldInclude.length
            ? tableBlock(el, `Missing from workscope (${shouldInclude.length})`, shouldInclude, [
                ["mpdTask", "MPD task"],
                ["mpdTitle", "Title"],
                ["mpdInterval", "Interval"],
                ["mpdManHours", "MH"],
                ["ldndNextDue", "LDND next due"],
                ["gapNote", "Note"],
                ["status", "Status"],
              ])
            : el("div", {
                class: "text-sm text-gray-500",
                text: result
                  ? "No MPD gaps flagged for this check type / effectivity slice (after LDND filter)."
                  : "Complete clauses (B) and effectivity (C) so a draft exists — gaps appear after simulation.",
              }),
          notDueYet.length
            ? tableBlock(el, `Not due yet (${notDueYet.length})`, notDueYet, [
                ["mpdTask", "MPD task"],
                ["mpdInterval", "Interval"],
                ["ldndNextDue", "Next due"],
                ["gapNote", "Note"],
              ])
            : null,
          wizardNav(el, {
            stage,
            onBack: () => goStage("WORKSCOPE"),
            onNext: () => goStage("REPORT"),
            nextLabel: "Next: Report →",
          }),
        ]),

        el("div", { class: `ws-card p-4 space-y-3 ws-wizard-panel${stage === "REPORT" || stage === "DONE" ? " is-on" : ""}` }, [
          el("div", { class: "text-sm font-semibold", text: "F — Confirm & report" }),
          el("p", {
            class: "text-xs text-gray-500",
            text: "Refresh the draft if answers or packages changed, then return to operator or approve.",
          }),
          el("div", { class: "grid sm:grid-cols-2 gap-3 text-sm" }, [
            summaryCell(el, "MR eligible", eligibleCount),
            summaryCell(el, "Not eligible / gaps", notEligible.length),
          ]),
          zeroTimeSummary?.required === true || zeroTimeSummary?.note
            ? el("div", {
                class: `text-xs rounded-lg border px-3 py-2 ${
                  zeroTimeSummary.fullyEligible === false
                    ? "border-amber-300 bg-amber-50 text-amber-950 font-semibold"
                    : "border-gray-200 bg-gray-50 text-gray-800"
                }`,
                text:
                  zeroTimeSummary.note ||
                  zeroTimeSummary.summary ||
                  "Lease requires zero-time / bring-back-to-zero for full MR eligibility.",
              })
            : null,
          el("div", { class: "flex flex-wrap gap-2" }, [
            actionBtn(
              el,
              "Refresh draft / report",
              async () => {
                msgEl.textContent = "Refreshing report…";
                msgEl.className = "text-sm text-gray-600";
                try {
                  await fetchJson(apiUrl(`/api/ai/workscopes/${id}/simulate`), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({}),
                  });
                  await paint();
                } catch (err) {
                  msgEl.textContent = cleanText(err?.message) || String(err);
                  msgEl.className = "text-sm text-red-700";
                }
              },
              "primary"
            ),
          ]),
          msgEl,
          el("div", { class: "ws-wizard-nav" }, [
            actionBtn(el, "← Back", () => goStage("MPD_GAPS"), "outline"),
            el("div", { class: "flex flex-wrap gap-2 ml-auto" }, [
              actionBtn(el, "Return to operator", async () => {
                await fetchJson(apiUrl(`/api/ai/workscopes/${id}/status`), {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ status: "RETURNED_TO_OPERATOR" }),
                });
                await paint();
              }, "outline"),
              actionBtn(el, "Approve", async () => {
                await fetchJson(apiUrl(`/api/ai/workscopes/${id}/status`), {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ status: "APPROVED" }),
                });
                await paint();
              }),
            ]),
          ]),
        ]),
      ])
    );
  }

  paint().catch((e) => {
    host.replaceChildren(
      el("div", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) })
    );
  });
}

function wizardNav(el, { stage, onBack, onNext, nextLabel = "Next →" }) {
  return el("div", { class: "ws-wizard-nav" }, [
    onBack
      ? actionBtn(el, "← Back", onBack, "outline")
      : el("span", { class: "text-xs text-gray-400", text: `Step ${stageLabel(stage)}` }),
    onNext ? actionBtn(el, nextLabel, onNext, "primary") : null,
  ]);
}

function actionBtn(el, label, onClick, tone = "default") {
  const cls =
    tone === "primary"
      ? "rounded-lg bg-dae text-white text-sm font-medium px-3 py-2"
      : tone === "outline"
        ? "rounded-lg border border-gray-200 bg-white text-sm font-medium px-3 py-2"
        : tone === "danger"
          ? "rounded-lg border border-red-200 text-red-800 bg-white text-sm px-3 py-2 hover:bg-red-50"
          : "rounded-lg border border-gray-200 bg-white text-sm px-3 py-2 hover:bg-gray-50";
  return el(
    "button",
    {
      type: "button",
      class: cls,
      onclick: () => {
        Promise.resolve(onClick()).catch((err) => {
          alert(err?.message || String(err));
        });
      },
    },
    [label]
  );
}

function summaryCell(el, label, value) {
  return el("div", {}, [
    el("div", { class: "text-xs text-gray-500", text: label }),
    el("div", { class: "text-lg font-semibold", text: value != null ? String(value) : "—" }),
  ]);
}

function tableBlock(el, title, rows, cols) {
  return el("div", { class: "ws-card overflow-hidden" }, [
    el("div", { class: "px-4 py-3 border-b border-gray-100 text-sm font-semibold", text: title }),
    el("div", { class: "overflow-x-auto max-h-[28rem]" }, [
      el("table", { class: "ws-table" }, [
        el(
          "thead",
          {},
          [el("tr", {}, cols.map(([, label]) => el("th", { text: label })))]
        ),
        el(
          "tbody",
          {},
          rows.slice(0, 300).map((row) =>
            el(
              "tr",
              {},
              cols.map(([key]) => {
                let v = row[key];
                if (typeof v === "boolean") v = v ? "Yes" : "No";
                return el("td", {
                  class: key.includes("Task") ? "font-mono text-xs" : "text-xs",
                  text: v == null || v === "" ? "—" : String(v).slice(0, 120),
                });
              })
            )
          )
        ),
      ]),
    ]),
  ]);
}
